import { Injectable } from '@angular/core';

export interface DeepLinkParams {
  amcoId?: string;
  filter?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {

  private readonly DEEP_LINK_STORAGE_KEY = 'deepLinkUrl';

  constructor() { }

  /**
   * Validates if the provided AmcoID is in a valid format
   * @param amcoId The AmcoID to validate
   * @returns True if the AmcoID is valid
   */
  private isValidAmcoId(amcoId: string): boolean {
    if (!amcoId || typeof amcoId !== 'string') {
      return false;
    }

    // Remove any whitespace
    const cleanAmcoId = amcoId.trim();

    // Check if it's not empty and contains only digits (for numeric AmcoIDs)
    // Also allow alphanumeric AmcoIDs if they exist
    if (cleanAmcoId.length === 0) {
      return false;
    }

    // Allow both numeric and alphanumeric AmcoIDs
    // Numeric: only digits
    // Alphanumeric: letters and digits
    const numericPattern = /^\d+$/;
    const alphanumericPattern = /^[a-zA-Z0-9]+$/;

    return numericPattern.test(cleanAmcoId) || alphanumericPattern.test(cleanAmcoId);
  }

  /**
   * Normalizes an AmcoID to a consistent format
   * @param amcoId The AmcoID to normalize
   * @returns Normalized AmcoID
   */
  private normalizeAmcoId(amcoId: string): string {
    if (!amcoId) {
      return '';
    }

    // Remove whitespace and convert to string
    let normalized = amcoId.toString().trim();

    // Remove any leading zeros for numeric AmcoIDs (optional - depends on your business logic)
    // Uncomment the following line if you want to remove leading zeros:
    // if (/^\d+$/.test(normalized)) {
    //   normalized = normalized.replace(/^0+/, '') || '0';
    // }

    return normalized;
  }

  /**
   * Extracts the path and parameters from a URL, handling different environments
   * @param url The full URL to parse
   * @returns Object containing path and parameters string
   */
  private extractPathAndParams(url: string): { path: string; paramsString: string } {
    try {
      // Handle both absolute and relative URLs
      let path: string;
      let paramsString: string = '';

      // Check if it's an absolute URL
      if (url.startsWith('http://') || url.startsWith('https://')) {
        // Parse the URL to get the pathname
        const urlObj = new URL(url);
        path = urlObj.pathname;
      } else {
        // It's a relative URL, use as is
        path = url;
      }

      // Find the tours path in the URL
      const toursIndex = path.indexOf('/tours');
      if (toursIndex === -1) {
        return { path: '', paramsString: '' };
      }

      // Extract everything after /tours
      const afterTours = path.substring(toursIndex + 6); // +6 for '/tours'

      // Check if there are parameters after /tours
      if (afterTours.startsWith(';')) {
        paramsString = afterTours;
      }

      const result = { path: path.substring(0, toursIndex + 6), paramsString };
      return result;
    } catch (error) {
      return { path: '', paramsString: '' };
    }
  }

  /**
   * Parses deep-link URL parameters from various URL formats
   * Supports:
   * - http://localhost:4200/tours;AmcoID=1036112;filter=All%20jobs
   * - https://amcotourtracking.supergrp.net/tours;AmcoID=1036112;filter=All%20jobs
   * - https://amcotourtrackingqa.supergrp.net/tours;AmcoID=1036112;filter=All%20jobs
   * - /tours;AmcoID=1036112;filter=All%20jobs
   * @param url The full URL to parse
   * @returns Parsed parameters object
   */
  parseDeepLinkParams(url: string): DeepLinkParams {
    const params: DeepLinkParams = {};

    try {
      const { paramsString } = this.extractPathAndParams(url);

      if (paramsString && paramsString.startsWith(';')) {
        const paramPairs = paramsString.substring(1).split(';');

        paramPairs.forEach(pair => {
          const [key, value] = pair.split('=');
          if (key && value) {
            const decodedValue = decodeURIComponent(value);
            switch (key.toLowerCase()) {
              case 'amcoid':
                const normalizedAmcoId = this.normalizeAmcoId(decodedValue);
                if (this.isValidAmcoId(normalizedAmcoId)) {
                  params.amcoId = normalizedAmcoId;
                } else {
                  console.warn(`Invalid AmcoID format: ${decodedValue}`);
                }
                break;
              case 'filter':
                params.filter = decodedValue;
                break;
            }
          }
        });
      }
    } catch (error) {
      console.error('Error parsing deep-link parameters:', error);
    }

    return params;
  }

  /**
   * Checks if the current URL is a deep-link for tours
   * Supports all environment URLs
   * @param url The URL to check
   * @returns True if it's a tours deep-link
   */
  isToursDeepLink(url: string): boolean {
    try {
      const { paramsString } = this.extractPathAndParams(url);
      return paramsString.startsWith(';') && (paramsString.toLowerCase().includes('amcoid=') || paramsString.toLowerCase().includes('filter='));
    } catch (error) {
      return false;
    }
  }

  /**
   * Creates a deep-link URL for tours
   * @param amcoId The AmcoID to search for (can be string or number)
   * @param filter The filter to apply (optional)
   * @param baseUrl Optional base URL (defaults to relative path)
   * @returns The deep-link URL
   */
  createToursDeepLink(amcoId: string | number, filter?: string, baseUrl?: string): string {
    let url = baseUrl ? `${baseUrl}/tours` : '/tours';
    const params: string[] = [];

    if (amcoId !== null && amcoId !== undefined && amcoId !== '') {
      const normalizedAmcoId = this.normalizeAmcoId(amcoId.toString());
      if (this.isValidAmcoId(normalizedAmcoId)) {
        params.push(`AmcoID=${encodeURIComponent(normalizedAmcoId)}`);
      } else {
        console.warn(`Invalid AmcoID provided for deep-link: ${amcoId}`);
      }
    }

    if (filter) {
      params.push(`filter=${encodeURIComponent(filter)}`);
    }

    if (params.length > 0) {
      url += ';' + params.join(';');
    }

    return url;
  }

  /**
   * Extracts AmcoID from various formats that might be in the URL
   * @param url The URL to extract AmcoID from
   * @returns The extracted AmcoID or null if not found
   */
  extractAmcoIdFromUrl(url: string): string | null {
    const params = this.parseDeepLinkParams(url);
    return params.amcoId || null;
  }

  /**
   * Validates a complete deep-link URL
   * @param url The URL to validate
   * @returns True if the URL is a valid tours deep-link
   */
  isValidToursDeepLink(url: string): boolean {
    if (!this.isToursDeepLink(url)) {
      return false;
    }

    const params = this.parseDeepLinkParams(url);
    return params.amcoId !== undefined && this.isValidAmcoId(params.amcoId);
  }

  /**
   * Gets the current environment base URL
   * @returns The base URL for the current environment
   */
  getCurrentBaseUrl(): string {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return '';
  }

  /**
   * Creates a deep-link URL for the current environment
   * @param amcoId The AmcoID to search for
   * @param filter The filter to apply (optional)
   * @returns The deep-link URL for the current environment
   */
  createToursDeepLinkForCurrentEnvironment(amcoId: string | number, filter?: string): string {
    const baseUrl = this.getCurrentBaseUrl();
    return this.createToursDeepLink(amcoId, filter, baseUrl);
  }

  /**
   * Stores the deep-link URL for later restoration after login
   * @param url The deep-link URL to store
   */
  storeDeepLinkUrl(url: string): void {
    if (this.isToursDeepLink(url)) {
      localStorage.setItem(this.DEEP_LINK_STORAGE_KEY, url);
    }
  }

  /**
   * Retrieves the stored deep-link URL
   * @returns The stored deep-link URL or null if not found
   */
  getStoredDeepLinkUrl(): string | null {
    const storedUrl = localStorage.getItem(this.DEEP_LINK_STORAGE_KEY);
    if (storedUrl && this.isToursDeepLink(storedUrl)) {
      return storedUrl;
    }
    if (storedUrl) {
      // Clear invalid stored URL
      this.clearStoredDeepLinkUrl();
    }
    return null;
  }

  /**
   * Clears the stored deep-link URL
   */
  clearStoredDeepLinkUrl(): void {
    const wasStored = localStorage.getItem(this.DEEP_LINK_STORAGE_KEY) !== null;
    localStorage.removeItem(this.DEEP_LINK_STORAGE_KEY);
    if (wasStored) {
    }
  }

  /**
   * Checks if there's a stored deep-link URL waiting to be restored
   * @returns True if there's a stored deep-link URL
   */
  hasStoredDeepLinkUrl(): boolean {
    return this.getStoredDeepLinkUrl() !== null;
  }
}
